import { useQuery } from '@tanstack/react-query';
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { BudgetStatus, Tool } from '@ace-tracker/shared';
import { fetchBudgetStatus, UnauthorizedError } from '../api';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { BUDGET_METRIC_INFO } from '../metricInfo';
import { useSettings } from '../settings';
import { ChartCard } from './ChartCard';
import { KpiCard } from './KpiCard';
import { TOOL_LABELS } from './Sidebar';
import { TableCard } from './TableCard';

const DASH = '—';

/** Over, near (≥ 90%), or within a limit; null when there's no limit or no projection. */
function standing(value: number | null, limit: number | null | undefined): 'over' | 'near' | 'within' | null {
  if (value === null || limit === null || limit === undefined) return null;
  if (value > limit) return 'over';
  return value >= limit * 0.9 ? 'near' : 'within';
}

function StandingText({ status, overText, withinText }: { status: ReturnType<typeof standing>; overText: string; withinText: string }) {
  if (status === null) return null;
  const icon = status === 'over' ? 'fa-circle-exclamation text-error' : status === 'near' ? 'fa-triangle-exclamation text-warn-ink' : 'fa-circle-check text-success';
  return (
    <span className="inline-flex items-center gap-1.5">
      <i aria-hidden="true" className={`fa-solid ${icon}`} />
      {status === 'over' ? overText : status === 'near' ? `Close to the limit — ${withinText}` : withinText}
    </span>
  );
}

function monthLabel(month: string): string {
  return new Date(`${month}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Cumulative cost so far, the run-rate line to month end, and the budget line. */
function cumulativeSeries(status: BudgetStatus) {
  const byDate = new Map(status.daily.map((d) => [d.date, d.cost]));
  const [year, month] = status.month.split('-').map(Number);
  const today = Math.max(1, Math.ceil(status.daysElapsed));
  const rate = status.projected === null ? null : status.projected / status.daysInMonth;
  let running = 0;
  const days = Array.from({ length: status.daysInMonth }, (_, i) => {
    const date = new Date(Date.UTC(year, month - 1, i + 1)).toISOString().slice(0, 10);
    running += byDate.get(date) ?? 0;
    return { date, total: running };
  });
  const todayTotal = days[today - 1]?.total ?? 0;
  // The run-rate line starts at today's actual so the two lines meet.
  return days.map(({ date, total }, i) => ({
    date,
    actual: i + 1 <= today ? total : null,
    projected: rate === null || i + 1 < today ? null : todayTotal + rate * (i + 1 - today),
  }));
}

export function BudgetSection({ money, SectionHeading }: { money: (value: number) => string; SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element }) {
  const { data: settings } = useSettings();
  const { data: status, isError, error } = useQuery({
    queryKey: ['dashboard-budget'],
    queryFn: fetchBudgetStatus,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err) => !(err instanceof UnauthorizedError) && failureCount < 2,
  });

  if (isError) return <p className="text-sm text-error">{(error as Error).message}</p>;
  if (!status) return null;

  const budgetStanding = standing(status.projected, status.budget);
  const perDeveloper = status.activeDevelopers > 0 ? status.monthToDate / status.activeDevelopers : null;
  const perDeveloperProjected = status.activeDevelopers > 0 && status.projected !== null ? status.projected / status.activeDevelopers : null;
  const perDeveloperGoal = settings?.targets.costPerDeveloperMax;
  const usedPercent = status.budget ? Math.min(100, (status.monthToDate / status.budget) * 100) : null;
  const projectedPercent = status.budget && status.projected !== null ? Math.min(100, (status.projected / status.budget) * 100) : null;
  const data = cumulativeSeries(status);

  return (
    <section>
      <SectionHeading title={`Budget — ${monthLabel(status.month)}`} hint="Calendar month, UTC · ignores the date range and filters" info={BUDGET_METRIC_INFO.budget} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="relative rounded-card border border-border bg-surface p-5 shadow-sm lg:col-span-2">
          <span className="absolute inset-y-0 left-0 w-[3px] rounded-l-card bg-primary-blue" />
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">Month to date</p>
            <p className="text-xs text-text-muted">
              Day {Math.ceil(status.daysElapsed)} of {status.daysInMonth}
            </p>
          </div>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-text-default">
            {money(status.monthToDate)}
            {status.budget !== null && <span className="text-base font-normal text-text-muted"> of {money(status.budget)} budget</span>}
          </p>
          {usedPercent !== null && (
            <div
              className="relative mt-3 h-3 overflow-hidden rounded-pill bg-surface-alt"
              role="img"
              aria-label={`${Math.round(usedPercent)}% of the budget used, ${projectedPercent === null ? 'no projection yet' : `${Math.round(projectedPercent)}% projected by month end`}`}
            >
              <div className={`h-full ${budgetStanding === 'over' ? 'bg-error' : 'bg-primary-blue'}`} style={{ width: `${usedPercent}%` }} />
              {projectedPercent !== null && <div className="absolute inset-y-0 w-[2px] bg-text-default" style={{ left: `calc(${projectedPercent}% - 1px)` }} />}
            </div>
          )}
          <p className="mt-3 text-sm text-text-default">
            Projected month end: <b className="font-semibold tabular-nums">{status.projected === null ? 'after the first day' : money(status.projected)}</b>
            {status.budget === null ? (
              <span className="text-text-muted"> · No monthly budget set — add one in Settings → Budgets & goals.</span>
            ) : (
              <>
                {' · '}
                <StandingText
                  status={budgetStanding}
                  overText={`over budget by ${money((status.projected ?? 0) - status.budget)}`}
                  withinText={`within budget, ${money(status.budget - (status.projected ?? 0))} to spare`}
                />
              </>
            )}
          </p>
          <p className="mt-1 text-xs text-text-muted">
            {status.byTool.map((t) => `${TOOL_LABELS[t.tool as Tool]} ${money(t.monthToDate)}`).join(' · ')}
            {status.partial && ` · Data starts ${status.from.slice(0, 10)} (retention), so month to date is understated; the projection uses the days available.`}
          </p>
        </div>
        <KpiCard
          label="Cost per active developer"
          value={perDeveloper === null ? DASH : money(perDeveloper)}
          meta={
            perDeveloper === null
              ? 'No activity this month yet'
              : `${status.activeDevelopers} developers · projected ${perDeveloperProjected === null ? DASH : money(perDeveloperProjected)}${perDeveloperGoal ? ` · goal ≤ ${money(perDeveloperGoal)}` : ''}`
          }
          flag={standing(perDeveloperProjected, perDeveloperGoal) === 'over' ? 'Above goal' : undefined}
          info={BUDGET_METRIC_INFO.costPerDeveloper}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <ChartCard title="Cumulative spend this month" caption="Actual so far, run rate to month end, and the budget" info={BUDGET_METRIC_INFO.cumulative}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ left: 8, right: 12, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
              <XAxis dataKey="date" {...chartXAxisTickProps} tickFormatter={(d: string) => d.slice(8)} />
              <YAxis {...chartAxisTickProps} width={64} tickFormatter={(v: number) => money(v)} domain={[0, (max: number) => Math.max(max, status.budget ?? 0)]} />
              <Tooltip {...chartTooltipProps} formatter={((value: number | null) => (value === null ? DASH : money(value))) as never} />
              <Legend {...chartLegendProps} />
              {status.budget !== null && (
                <ReferenceLine
                  y={status.budget}
                  ifOverflow="extendDomain"
                  stroke="var(--text-muted)"
                  strokeDasharray="5 4"
                  label={{ value: `Budget ${money(status.budget)}`, position: 'insideTopLeft', fill: 'var(--text-muted)', fontSize: 11 }}
                />
              )}
              <Line type="monotone" dataKey="actual" name="Actual" stroke="var(--primary-blue)" strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="projected" name="Run rate" stroke="var(--primary-blue)" strokeDasharray="4 4" strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <TableCard title="Team budgets" info={BUDGET_METRIC_INFO.teams} empty={status.teams.length === 0} emptyText="No team has enough active developers this month. Assign teams in Settings → Developers.">
          <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
            <tr>
              <th className="pb-2.5 pr-3">Team</th>
              <th className="pb-2.5 pr-3 text-right">Developers</th>
              <th className="pb-2.5 pr-3 text-right">Month to date</th>
              <th className="pb-2.5 pr-3 text-right">Projected</th>
              <th className="pb-2.5 pr-3 text-right">Budget</th>
              <th className="pb-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {status.teams.map((t) => (
              <tr key={t.team} className="border-b border-border last:border-0">
                <td className="py-2.5 pr-3 text-text-default">{t.team}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{t.developers}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{money(t.monthToDate)}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{t.projected === null ? DASH : money(t.projected)}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{t.budget === null ? DASH : money(t.budget)}</td>
                <td className="py-2.5 text-xs">
                  {t.budget === null ? (
                    <span className="text-text-faint">No budget</span>
                  ) : (
                    <StandingText status={standing(t.projected, t.budget)} overText="Projected over" withinText="On track" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableCard>
      </div>
      {status.hiddenTeams.length > 0 && (
        <p className="mt-2 text-xs text-text-muted">
          Budgets set for {status.hiddenTeams.join(', ')} are not shown: fewer active developers than the minimum team size (Settings → Executive), so the figure could identify individuals.
        </p>
      )}
    </section>
  );
}
